import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import MyApiClient from '../../../../api-client/MyApiClient';
import { usePageTitle, useStatoInSolaLettura } from '../../../layouts/PageHeaderExtra';
import { caricaDocumentiDaValidare } from './documentiDaValidare';

/*
 * Scheda del documento da validare (03/10/2026): «Scarica», «Convalida» e «Respingi» erano nel menu a fine
 * riga della lista «Documenti da validare», ora stanno qui. Il documento arriva dalla lista (navigate
 * state) e si rilegge dagli elenchi dei documenti in attesa, cercandolo per id e tipo.
 */

const LIST_URL = '/superadmin/documenti';

const getToken = () => {
    const dettagli = sessionStorage.getItem('userDetails');
    return dettagli ? JSON.parse(dettagli).idToken : null;
};

export const SchedaDocumento = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const tipo = location.state?.documento?.documentType;

    const [documento, setDocumento] = useState(location.state?.documento || null);
    const [caricato, setCaricato] = useState(false);
    const [loadingShowed, setLoadingShowed] = useState(false);
    const [errore, setErrore] = useState('');
    // 'convalida' | 'respingi'
    const [conferma, setConferma] = useState(null);

    useEffect(() => {
        const carica = async () => {
            const token = getToken();
            if (!token) return;
            try {
                const documenti = await caricaDocumentiDaValidare(new MyApiClient(), token);
                const trovato = documenti.find(d => String(d.documentId) === String(id) && (!tipo || d.documentType === tipo));
                // Già convalidato o respinto: non è più tra quelli da validare
                setDocumento(trovato || null);
            } catch (e) {
                console.error(e);
            } finally {
                setCaricato(true);
            }
        };
        carica();
    }, [id, tipo]);

    usePageTitle(documento ? `${documento.tipologia} di ${documento.name}` : '');
    useStatoInSolaLettura(documento ? 'Da validare' : '', 'bozza');

    const indietro = () => navigate(LIST_URL);

    if (!documento) {
        return (
            <>
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        {caricato ? 'Documento non trovato: potrebbe essere già stato convalidato o respinto.' : 'Caricamento in corso...'}
                    </Card.Body>
                </Card>
                <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                    <span />
                    <button type="button" className="btn btn-dark light" onClick={indietro}>Indietro</button>
                </div>
            </>
        );
    }

    const esegui = async (azione) => {
        setConferma(null);
        const token = getToken();
        if (!token) return;
        const apiClient = new MyApiClient();
        setLoadingShowed(true);
        const chiamata = azione === 'convalida' ? apiClient.approveDocument : apiClient.rejectDocument;
        const esito = await chiamata.call(apiClient, token, documento.documentId, documento.verificationCode, documento.userId, documento.documentType);
        setLoadingShowed(false);
        if (esito && esito.error) {
            setErrore('Si è verificato un errore durante il salvataggio (' + esito.error + ')');
            return;
        }
        navigate(LIST_URL);
    };

    const descrizione = `${documento.tipologia.toLowerCase()} di «${documento.name}»`;

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <Modal className="fade" show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>

            <Modal className="fade" show={!!conferma} onHide={() => setConferma(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{conferma === 'respingi' ? 'Respingere il documento?' : 'Convalidare il documento?'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {conferma === 'respingi' ? `Respingere ${descrizione}?` : `Convalidare ${descrizione}?`}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConferma(null)}>Annulla</Button>
                    <Button variant={conferma === 'respingi' ? 'danger' : 'primary'} onClick={() => esegui(conferma)}>
                        {conferma === 'respingi' ? 'Respingi' : 'Convalida'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <h5 className="sid-form-section">Documento</h5>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label>Nome documento</Form.Label>
                            <div className="sid-readonly-value">{documento['nome-documento'] || '—'}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Tipologia</Form.Label>
                            <div className="sid-readonly-value">{documento.tipologia || '—'}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Sport</Form.Label>
                            <div className="sid-readonly-value">{documento.sport || '—'}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Data caricamento</Form.Label>
                            <div className="sid-readonly-value">{documento['data-caricamento'] || '—'}</div>
                        </Col>
                    </Row>

                    <h5 className="sid-form-section mt-4">Caricato da</h5>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label>Nome e cognome</Form.Label>
                            <div className="sid-readonly-value">{documento.name || '—'}</div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                <button type="button" className="btn btn-danger light" onClick={() => setConferma('respingi')}>Respingi</button>
                <div className="d-flex gap-4">
                    <button type="button" className="btn btn-dark light" onClick={indietro}>Indietro</button>
                    {documento.uri && (
                        <button type="button" className="btn btn-dark light" onClick={() => window.open(documento.uri, '_blank')}>Scarica</button>
                    )}
                    <Button className="btn btn-primary" onClick={() => setConferma('convalida')}>Convalida</Button>
                </div>
            </div>
        </>
    );
};

export default SchedaDocumento;
